/**
 * IletisimTekrarlariPage.jsx — Telefon / e-posta tekrar raporu (backlog #491, backend #490). Rota: /Users/IletisimTekrarlari (page.kullanici).
 *
 * Telefon ve e-posta kullanıcıda benzersiz DEĞİL (bir kişinin birden fazla hesabı olabilir; veri tabanı anahtarı yok, karar 2026-10-18):
 * bunun yerine yetkililer bu raporla aynı değeri paylaşan kullanıcı gruplarına bakar. `GET /users/iletisim-tekrarlari`:
 *   · alan TELEFON (son 10 hane karşılaştırılır) | EPOSTA (kırpılmış, küçük harf)
 *   · sadeceAktif (varsayılan açık) — pasif hesapları gizler
 *   · ayniKisiGizle — "tek kişinin firma başına bir hesabı" olan grupları (`ayniKisi`) listeden çıkarır; gerçek çakışmalar önde gelir
 * Her grup bir satır kartı: anahtar, kullanıcı / aktif sayısı, Aynı kişi / Çakışma rozeti ve kullanıcıların kısa tablosu (Kullanıcı Kartı
 * bağlantısı, firma, rol, durum, son giriş, bağlı kişi). Aynı kişi grupları kapalı, çakışmalar açık başlar.
 *
 * Alan, iki süzgeç ve sayfa adres çubuğundadır: karttan dönüşte yerinde durur. Salt okunur — düzeltme Kullanıcı Kartı › Düzenle'de yapılır.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  Alert, Badge, Button, ButtonGroup, Card, CardBody, Col, Container, FormGroup, Input, Label, Pagination, PaginationItem, PaginationLink, Row, Spinner, Table,
} from "reactstrap";
import { ArrowLeft, ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { kisiSayfasiUrl } from "../../../Components/Kisi/kisiBaglantilari";
import { AktifRozeti, tarihSaat } from "../../../Components/Kullanici/KullaniciParcalari";
import { getIletisimTekrarlari } from "../../../services/userService";
import i18n, { currentLocaleTag } from "../../../i18n";

const SAYFA_BOYU = 50;

function GrupKarti({ grup, alan, acik, onToggle, geri, geriEtiket }) {
  const { t } = useTranslation("yonetim");
  const kullanicilar = grup.kullanicilar || [];
  return (
    <Card className="mb-3">
      <CardBody className="py-2">
        <button
          type="button" className="btn btn-link text-body text-decoration-none p-0 w-100 text-start d-flex align-items-center gap-2 flex-wrap"
          onClick={onToggle} aria-expanded={acik}
        >
          {acik ? <ChevronDown size={16} className="flex-shrink-0" /> : <ChevronRight size={16} className="flex-shrink-0" />}
          <span className="fw-semibold font-monospace text-break">{grup.anahtar}</span>
          <Badge color="light" className="text-body border fw-normal">{t("iletisimTekrarlari.kullaniciSayisi", { count: grup.kullaniciSayisi })}</Badge>
          <Badge color="light" className="text-body border fw-normal">{t("iletisimTekrarlari.aktifSayisi", { count: grup.aktifSayisi })}</Badge>
          {grup.ayniKisi
            ? <Badge color="secondary" className="fw-normal" title={t("iletisimTekrarlari.ayniKisiIpucu")}>{t("iletisimTekrarlari.ayniKisi")}</Badge>
            : <Badge color="warning" className="fw-normal" title={t("iletisimTekrarlari.cakismaIpucu")}>{t("iletisimTekrarlari.cakisma")}</Badge>}
        </button>

        {acik && (
          <div className="table-responsive mt-2">
            <Table className="table-sm align-middle mb-0">
              <thead className="table-light text-muted small">
                <tr>
                  <th>{t("iletisimTekrarlari.kullanici")}</th>
                  <th>{alan === "EPOSTA" ? t("iletisimTekrarlari.eposta") : t("iletisimTekrarlari.telefon")}</th>
                  <th>{t("iletisimTekrarlari.firma")}</th>
                  <th>{t("iletisimTekrarlari.rol")}</th>
                  <th>{t("iletisimTekrarlari.durum")}</th>
                  <th>{t("iletisimTekrarlari.sonGiris")}</th>
                  <th>{t("iletisimTekrarlari.bagliKisi")}</th>
                </tr>
              </thead>
              <tbody>
                {kullanicilar.map((u) => (
                  <tr key={u.userId}>
                    <td>
                      <Link
                        to={`/Users/${u.userId}?geri=${encodeURIComponent(geri)}&geriEtiket=${encodeURIComponent(geriEtiket)}`}
                        className="fw-semibold text-body" title={t("iletisimTekrarlari.kullaniciKarti")}
                      >
                        {u.adSoyad?.trim() || u.kullaniciAdi || t("iletisimTekrarlari.kullaniciNo", { id: u.userId })}
                      </Link>
                      <div className="small text-muted font-monospace">{u.kullaniciAdi} · #{u.userId}</div>
                    </td>
                    <td className="small text-break">{(alan === "EPOSTA" ? u.eposta : u.telefon) || "—"}</td>
                    <td className="small">
                      {u.firmaAdi ? (
                        <>
                          {u.firmaAdi}
                          {u.firmaSayisi > 1 && <span className="text-muted"> +{u.firmaSayisi - 1}</span>}
                        </>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td className="small">{u.grupAdi || <span className="text-muted">—</span>}</td>
                    <td><AktifRozeti u={{ userActive: u.aktif ? 1 : 0 }} /></td>
                    <td className="small text-nowrap">{tarihSaat(u.sonGiris) || <span className="text-muted">—</span>}</td>
                    <td className="small">
                      {u.bagliPersonId != null ? (
                        <Link
                          to={kisiSayfasiUrl({ personId: u.bagliPersonId, tab: "kullanicilar", geri, geriEtiket })}
                          className="fw-semibold" title={t("iletisimTekrarlari.kisiSayfasiniAc")}
                        >
                          {u.bagliAdSoyad || t("iletisimTekrarlari.kisiNo", { id: u.bagliPersonId })}
                        </Link>
                      ) : <span className="text-muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default function IletisimTekrarlariPage() {
  const { t } = useTranslation("yonetim");
  const location = useLocation();
  const [sp, setSp] = useSearchParams();

  // Durum adres çubuğundan türetilir (ayrı state yok): alan, "pasifler de" ve "aynı kişiyi gizle" süzgeçleri, sayfa.
  const alan = sp.get("alan") === "EPOSTA" ? "EPOSTA" : "TELEFON";
  const sadeceAktif = sp.get("pasiflerDahil") !== "1";
  const ayniKisiGizle = sp.get("ayniKisiGizle") === "1";
  const sayfa = Math.max(1, Number(sp.get("sayfa")) || 1);

  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [error, setError] = useState(null);
  // Elle açılan / kapatılan gruplar (anahtar → açık mı); dokunulmayanlar varsayılanı izler: gerçek çakışma açık, aynı kişi kapalı.
  const [acik, setAcik] = useState({});

  useEffect(() => { document.title = i18n.t("yonetim:iletisimTekrarlari.sayfaBasligi"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setError(null);
      try {
        const d = await getIletisimTekrarlari({ alan, sadeceAktif, ayniKisiGizle, page: sayfa - 1, size: SAYFA_BOYU });
        if (ignore) return;
        const sonSayfa = Math.max(1, Math.ceil((d?.toplam ?? 0) / SAYFA_BOYU));
        if ((d?.toplam ?? 0) > 0 && sayfa > sonSayfa) {
          const next = new URLSearchParams(sp);
          next.set("sayfa", String(sonSayfa));
          setSp(next, { replace: true });
          return;
        }
        setVeri(d);
        setAcik({});
      } catch (e) {
        if (!ignore) { setError(e.message || i18n.t("yonetim:iletisimTekrarlari.raporOkunamadi")); setVeri(null); }
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    run();
    return () => { ignore = true; };
    // `sp` bilerek bağımlılık dışı: yalnızca sayfa düzeltmesi için okunur, her adres değişiminde yeniden okuma istenmez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alan, sadeceAktif, ayniKisiGizle, sayfa]);

  function guncelle(patch) {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    if (!("sayfa" in patch)) next.delete("sayfa");
    setSp(next, { replace: true });
  }

  const ozet = veri?.ozet;
  const gruplar = veri?.gruplar || [];
  const toplam = veri?.toplam ?? 0;
  const toplamSayfa = Math.max(1, Math.ceil(toplam / SAYFA_BOYU));
  const geri = `${location.pathname}${location.search}`;
  const geriEtiket = t("iletisimTekrarlari.rapora");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("iletisimTekrarlari.baslik")} pageTitle={t("iletisimTekrarlari.kullanicilar")} />

        <Link to="/Users" className="d-inline-flex align-items-center gap-1 mb-3 text-decoration-none">
          <ArrowLeft size={14} />{t("iletisimTekrarlari.kullanicilaraDon")}
        </Link>

        <Card className="mb-3">
          <CardBody>
            <Row className="g-3 align-items-center">
              <Col md="auto">
                <ButtonGroup>
                  <Button color={alan === "TELEFON" ? "primary" : "light"} onClick={() => guncelle({ alan: "" })}>{t("iletisimTekrarlari.telefon")}</Button>
                  <Button color={alan === "EPOSTA" ? "primary" : "light"} onClick={() => guncelle({ alan: "EPOSTA" })}>{t("iletisimTekrarlari.eposta")}</Button>
                </ButtonGroup>
              </Col>
              <Col md="auto">
                <FormGroup check className="mb-0">
                  <Input id="it-pasifler" type="checkbox" checked={!sadeceAktif} onChange={(e) => guncelle({ pasiflerDahil: e.target.checked ? "1" : "" })} />
                  <Label for="it-pasifler" check>{t("iletisimTekrarlari.pasiflerDahil")}</Label>
                </FormGroup>
              </Col>
              <Col md="auto">
                <FormGroup check className="mb-0">
                  <Input id="it-ayni" type="checkbox" checked={ayniKisiGizle} onChange={(e) => guncelle({ ayniKisiGizle: e.target.checked ? "1" : "" })} />
                  <Label for="it-ayni" check>{t("iletisimTekrarlari.ayniKisiGizle")}</Label>
                </FormGroup>
              </Col>
              {ozet && (
                <Col className="d-flex gap-2 flex-wrap justify-content-md-end">
                  <Badge color="light" className="text-body border fw-normal">{t("iletisimTekrarlari.ozetGruplar", { count: ozet.gruplar })}</Badge>
                  <Badge color="light" className="text-body border fw-normal">{t("iletisimTekrarlari.ozetKullanicilar", { count: ozet.kullanicilar })}</Badge>
                  <Badge color="warning" className="fw-normal">{t("iletisimTekrarlari.ozetCakisma", { count: ozet.cakismaGruplari })}</Badge>
                  <Badge color="secondary" className="fw-normal">{t("iletisimTekrarlari.ozetAyniKisi", { count: ozet.ayniKisiGruplari })}</Badge>
                </Col>
              )}
            </Row>
          </CardBody>
        </Card>

        {error && <Alert fade={false} color="danger" toggle={() => setError(null)}>{error}</Alert>}

        {yukleniyor && !veri && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{t("common:yukleniyor")}</p>
          </div>
        )}

        {!yukleniyor && !error && gruplar.length === 0 && <Alert fade={false} color="secondary">{t("iletisimTekrarlari.tekrarYok")}</Alert>}

        <div style={yukleniyor && veri ? { opacity: 0.5 } : undefined}>
          {gruplar.map((g) => (
            <GrupKarti
              key={g.anahtar} grup={g} alan={alan} geri={geri} geriEtiket={geriEtiket}
              acik={acik[g.anahtar] ?? !g.ayniKisi}
              onToggle={() => setAcik((a) => ({ ...a, [g.anahtar]: !(a[g.anahtar] ?? !g.ayniKisi) }))}
            />
          ))}
        </div>

        {toplamSayfa > 1 && (
          <div className="d-flex flex-column align-items-center p-3">
            <Pagination>
              <PaginationItem disabled={sayfa <= 1}>
                <PaginationLink previous onClick={() => guncelle({ sayfa: String(sayfa - 1) })} />
              </PaginationItem>
              <PaginationItem active>
                <PaginationLink>{sayfa}</PaginationLink>
              </PaginationItem>
              <PaginationItem disabled={sayfa >= toplamSayfa}>
                <PaginationLink next onClick={() => guncelle({ sayfa: String(sayfa + 1) })} />
              </PaginationItem>
            </Pagination>
            <div className="text-muted small">
              {t("iletisimTekrarlari.sayfaBilgisi", {
                sayfa, toplamSayfa: toplamSayfa.toLocaleString(currentLocaleTag()), toplam: toplam.toLocaleString(currentLocaleTag()),
              })}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}
